
import React from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogClose,
} from "@/components/ui/dialog";
import { Form } from "@/components/ui/form"; // Import the Form component
import { InteractionFormFields } from './components/InteractionFormFields';
import { useInteractionSubmit } from './hooks/useInteractionSubmit';
import type { InteractionFormValues } from '@/components/candidates/types';

interface AddInteractionDialogProps {
  candidateId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onInteractionAdded?: () => void;
}

const interactionFormSchema = z.object({
  type: z.enum(['note', 'email', 'call', 'meeting', 'offer_letter', 'document']),
  title: z.string().min(1, "Title is required"),
  content: z.string().min(1, "Content is required"),
  document: z.instanceof(File).optional(),
});

export type InteractionFormData = z.infer<typeof interactionFormSchema>; // Exporting for use in InteractionFormFields

export const AddInteractionDialog: React.FC<AddInteractionDialogProps> = ({
  candidateId,
  open,
  onOpenChange,
  onInteractionAdded,
}) => {
  const form = useForm<InteractionFormData>({
    resolver: zodResolver(interactionFormSchema),
    defaultValues: {
      type: 'note', // Ensure 'type' is always initialized
      title: '',
      content: '',
      document: undefined,
    },
  });

  const { mutate: createInteraction, isPending } = useInteractionSubmit(candidateId, () => {
    onOpenChange(false);
    if (onInteractionAdded) {
      onInteractionAdded();
    }
    form.reset();
  });

  const handleSubmit = (values: InteractionFormData) => {
    const submissionData: Omit<InteractionFormValues, 'candidate_id'> = {
        type: values.type,
        title: values.title,
        content: values.content,
        document: values.document,
    };
    createInteraction(submissionData);
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => {
      onOpenChange(isOpen);
      if (!isOpen) {
        form.reset();
      }
    }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add New Interaction</DialogTitle>
        </DialogHeader>
        {/* Wrap InteractionFormFields with Form provider */}
        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="py-4 space-y-4">
            <InteractionFormFields form={form} />
            <DialogFooter>
              <DialogClose asChild>
                <Button type="button" variant="outline" disabled={isPending} onClick={() => form.reset()}>Cancel</Button>
              </DialogClose>
              <Button type="submit" disabled={isPending}>
                {isPending ? 'Saving...' : 'Save Interaction'}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

